import React, { createContext, useEffect, useReducer, useState } from 'react';
// reducers
import {
  dataInitialState,
  dataReducer,
  sortInitialState,
  sortReducer,
} from 'src/@complaints/reducers';
// interfaces
import { IFilterState, IStoreState } from 'src/@complaints/interfaces';
import { filterInitialState, filterReducer } from 'src/@complaints/reducers/filterReducer';
// Hooks
import { usePersistentReducer } from 'src/utils';
import { serializeFiltersToUrl, useParseUrlFilters } from 'src/utils/filters/parseUrlFilters';
import {
  filterKeyMap,
  tableFiltersToComplaintSummaryFilter,
  urlToComplaintFilterState,
} from '../reducers/constants';
import { ComplaintSummaryFilter } from 'src/app/gql/__generated__/hooks';
import { useHistory, useLocation } from 'react-router-dom';

export const ComplaintsStateContext = createContext({
  data: dataInitialState,
  sort: sortInitialState,
  filters: filterInitialState,
});

export const ComplaintsDispatchContext = createContext<React.Dispatch<any> | null>(null);

export const ComplaintsStateProvider = ({ children }) => {
  const [dataState, dataDispatch] = useReducer(dataReducer, dataInitialState);
  const [sortState, sortDispatch] = useReducer(sortReducer, sortInitialState);

  const urlFilterState = useParseUrlFilters<ComplaintSummaryFilter>(filterKeyMap);
  const storageMechanism = Object.keys(urlFilterState).length > 0 ? 'url' : 'session';
  const urlState = urlToComplaintFilterState(urlFilterState);

  const combinedFilterState: IFilterState = {
    ...filterInitialState,
    isLoadedFromUrl: !!Object.keys(urlFilterState).length,
    filters: {
      ...filterInitialState.filters,
      ...urlState.filters,
    },
    time: urlState.time,
    complaintID: urlState.complaintID,
    complainer: urlState.complainer,
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [filterState, filterDispatch] = usePersistentReducer(
    filterReducer,
    combinedFilterState,
    'complaints.filter.state',
    storageMechanism
  );

  const state: IStoreState = {
    data: dataState,
    sort: sortState,
    filters: filterState,
  };

  const dispatchers = [dataDispatch, sortDispatch, filterDispatch];

  const dispatch: (action: string) => void = (action: string) =>
    dispatchers.map((dispatcher: React.Dispatch<any>) => {
      dispatcher(action);
    });

  const location = useLocation();
  const history = useHistory();
  const [defaultLoad, setDefaultLoad] = useState(true);

  useEffect(() => {
    if (defaultLoad) {
      setDefaultLoad(false);
      return;
    } else {
      if (defaultLoad) {
        setDefaultLoad(false);
      }
    }

    // Create a clean filter object excluding internal fields
    const filtersForUrl = { ...state.filters.filters };
    const timeRangeForUrl = state.filters.time;

    const { filter } = tableFiltersToComplaintSummaryFilter(
      filtersForUrl,
      timeRangeForUrl,
      state.filters.complaintID,
      state.filters.complainer
    );

    const urlSearchParams = serializeFiltersToUrl(
      {
        ...filter,
      },
      filterKeyMap,
      true
    );
    const currentSearch = location.search.substring(1); // Remove leading '?'
    // Only update URL if the search params have actually changed
    if (urlSearchParams !== currentSearch) {
      const newPath = `${location.pathname}${urlSearchParams ? `?${urlSearchParams}` : ''}`;
      history.replace(newPath, location.state);
    }
  }, [state.filters, location.pathname, location.search, location.state, history]);

  return (
    <ComplaintsStateContext.Provider value={state}>
      <ComplaintsDispatchContext.Provider value={dispatch}>
        {children}
      </ComplaintsDispatchContext.Provider>
    </ComplaintsStateContext.Provider>
  );
};
